import { useState, useEffect, useRef } from "react";
import { pushEvent } from "@/utils/dataLayer";

const ROTATING_WORDS = ["BROKEN", "FRAGILE", "GENERIC", "LEAKY", "WRONG"];

const HeroSection = () => {
  const [currentWord, setCurrentWord] = useState(0);
  const wordRef = useRef<HTMLSpanElement>(null);
  const heroContentRef = useRef<HTMLDivElement>(null);
  const scrollGuideRef = useRef<HTMLDivElement>(null);

  // Rotating word animation
  useEffect(() => {
    const interval = setInterval(() => {
      if (wordRef.current) {
        wordRef.current.classList.add("rotating-out");
        setTimeout(() => {
          setCurrentWord((prev) => {
            const next = (prev + 1) % ROTATING_WORDS.length;
            if (next === ROTATING_WORDS.length - 1 && window.scrollY > 600) {
              clearInterval(interval);
            }
            return next;
          });
          if (wordRef.current) {
            wordRef.current.classList.remove("rotating-out");
            wordRef.current.classList.add("rotating-in");
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                wordRef.current?.classList.remove("rotating-in");
              });
            });
          }
        }, 400);
      }
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  // Scroll-driven hero fade + parallax
  useEffect(() => {
    const onScroll = () => {
      const scrollY = window.scrollY;
      if (scrollY < 600) {
        const opacity = Math.max(0, 1 - scrollY / 400);
        const heroEls = heroContentRef.current?.querySelectorAll(".hero-h1, .hero-sub");
        heroEls?.forEach((el) => {
          (el as HTMLElement).style.opacity = String(opacity);
          (el as HTMLElement).style.transform = `translateY(${scrollY * 0.2}px)`;
        });
        if (scrollGuideRef.current) {
          scrollGuideRef.current.style.opacity = String(Math.max(0, 1 - scrollY / 200));
        }
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section className="hero-section" ref={heroContentRef}>
      <div className="schematic-bg" />

      {/* Desktop hero */}
      <h1
        className="hero-h1 font-display font-black uppercase hidden sm:block"
        style={{ fontSize: "clamp(4rem, 8vw, 7.5rem)", lineHeight: 0.9, marginBottom: "24px" }}
      >
        GREAT BUSINESS
      </h1>
      <div
        className="hero-sub font-display font-black uppercase hidden sm:flex items-center gap-2 md:gap-4 flex-wrap"
        style={{ fontSize: "clamp(2.5rem, 8vw, 7.5rem)", lineHeight: 0.9 }}
      >
        <span className="hero-dots">...</span>
        <span ref={wordRef} className="hero-rotating-text text-void-muted">
          {ROTATING_WORDS[currentWord]}
        </span>
        <span className="text-void-text-subtle">WEBSITE</span>
      </div>

      {/* Mobile hero — all words visible, color cycles between them */}
      <div
        className="hero-mobile-stack sm:hidden font-display font-black uppercase"
        style={{ lineHeight: 1.05, fontSize: "clamp(3.2rem, 16vw, 5.5rem)" }}
      >
        <div className="text-foreground">GREAT</div>
        <div className="text-foreground">BUSINESS <span className="text-primary">//</span></div>
        <div className="mt-2">
          <div className="mobile-highlight mobile-highlight-1">BROKEN</div>
          <div className="mobile-highlight mobile-highlight-2">LEAKY</div>
          <div className="mobile-highlight mobile-highlight-3">WRONG</div>
        </div>
        <div className="text-void-text-subtle">WEBSITE</div>
      </div>

      <button
        className="hero-btn"
        onClick={() => {
          pushEvent('cta_click', { label: 'I WANT TO FIX THIS', location: 'hero' });
          document.getElementById('diagnostic')?.scrollIntoView({ behavior: 'smooth' });
        }}
      >
        I WANT TO FIX THIS
      </button>

      <div className="scroll-guide" ref={scrollGuideRef} />
    </section>
  );
};

export default HeroSection;
